/**
 * @fileoverview AppRoutes - Main routing configuration with lazy loading and protection
 * @module routes/AppRoutes
 */

import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ROUTE_MANIFEST } from './routeManifest';
import { AuthProvider, useAuth } from '@/providers/AuthProvider';
import { Spinner } from '@/components/loaders';
import { DashboardLayout } from '@/components/layout';

/**
 * ProtectedRoute - Wrapper component for routes requiring authentication
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresAuth: boolean;
  roles?: string[];
}

function ProtectedRoute({ children, requiresAuth, roles }: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isInitialized, user } = useAuth();

  // Public routes - render immediately
  if (!requiresAuth) {
    return <>{children}</>;
  }

  // Do not redirect while session metadata is being restored after a reload/login.
  if (!isInitialized) {
    return <LoadingFallback />;
  }

  // Check authentication
  if (!isAuthenticated || !user) {
    // Redirect to login, preserving intended destination
    const currentPath = window.location.pathname;
    const redirectUrl = `/auth/login?redirect=${encodeURIComponent(currentPath)}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // Check role-based access control
  if (roles && roles.length > 0) {
    const hasAccess = roles.some((role) => user.roles?.includes(role));

    if (!hasAccess) {
      // User is authenticated but lacks required roles
      return <Navigate to="/401" replace />;
    }
  }

  // User is authenticated and has required roles
  return <>{children}</>;
}

/**
 * LoadingFallback - Loading spinner shown during route transitions
 */
function LoadingFallback(): JSX.Element {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Spinner size="lg" label="Loading page..." />
    </div>
  );
}

/**
 * AppRoutes - Main routing component with lazy loading and authentication
 */
export function AppRoutes(): JSX.Element {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Dashboard routes with layout */}
            {/* The wildcard is required so every nested catalog/detail URL is owned by the SPA layout. */}
            <Route path="/app/*" element={<DashboardLayout />}>
              {ROUTE_MANIFEST.filter((route) => route.meta.layout === 'dashboard').map((route) => {
                const RouteElement = route.element;
                // Remove /app prefix from path for nested routes
                const nestedPath = route.path.replace('/app/', '');

                return (
                  <Route
                    key={route.id}
                    path={nestedPath}
                    element={
                      <ProtectedRoute
                        requiresAuth={route.meta.requiresAuth}
                        roles={route.meta.roles}
                      >
                        <RouteElement />
                      </ProtectedRoute>
                    }
                  />
                );
              })}
            </Route>

            {/* Non-dashboard routes */}
            {ROUTE_MANIFEST.filter((route) => route.meta.layout !== 'dashboard').map((route) => {
              const RouteElement = route.element;

              return (
                <Route
                  key={route.id}
                  path={route.path}
                  element={
                    <ProtectedRoute requiresAuth={route.meta.requiresAuth} roles={route.meta.roles}>
                      <RouteElement />
                    </ProtectedRoute>
                  }
                />
              );
            })}

            {/* 404 Fallback - Catch all unmatched routes */}
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Export default for convenience
 */
export default AppRoutes;
